import { Component, OnInit, Input, OnDestroy, forwardRef } from '@angular/core';
import { TemplateService } from '../../../../../services/template.service';
import { Number2textThService } from '../../../../../services/number2text-th.service';
import {
  AsyncValidatorFn,
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { combineLatest, Observable, of,  timer, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { LineItem, Product, paymentMethod } from '../../document-issue.model';
import {ApiService} from '../../../../../services/api.service';
import Decimal from 'decimal.js-light';
import { TranslateService } from '@ngx-translate/core';
import { CALENDAR_LOCALE } from '../../../../../services/constant';
import * as moment from 'moment';


@Component({
  selector: 'app-line-item-form',
  templateUrl: './line-item-form.component.html',
  styleUrls: ['./line-item-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LineItemFormComponent),
      multi: true
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => LineItemFormComponent),
      multi: true
    },
    TemplateService,
    Number2textThService
  ]
})
export class LineItemFormComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  onChange: any = () => { };
  onTouched: any = () => { };

  @Input() documentIssueForm: any;
  @Input() documentInformation: any;
  @Input() documentType: string;
  @Input() isSubmitted: boolean;
  @Input() abbData: any;

  lineItemForm: FormGroup;
  subscription: Subscription;

  countries: any[];
  documentTypes: any[];

  isSellerHeadOffice = false;
  isBuyerHeadOffice = false;
  isDisableAddDiscount = false;
  vats = [
    { label: 'Non-Vat', value: 'non-vat' },
    { label: 'Vat 0%', value: '0' },
    { label: 'Vat 7%', value: '7' }
  ];

  vatValue = {
    '0': 0,
    '7': 0.07
  }

  receiptVats = [
    { label: 'Empty', value: '' },
    { label: 'Non-Vat', value: 'non-vat' },
    { label: 'Vat 0%', value: '0' },
    { label: 'Vat 7%', value: '7' }
  ];

  paymentChoices = [];
  creditCardChoices = [
    {label: "Visa", value: "Visa"},
    {label: "Master", value: "Master"},
    {label: "American Express", value: "American Express"},
    {label: "JCB", value: "JCB"},
    {label: "Union Pay", value: "Union Pay"},
    {label: "Alipay & Wechat", value: "Alipay & Wechat"},
    {label: "Diners Club", value: "Diners Club"},
  ];
  bankChoices = [
    {label: "SCB", value: "SCB"},
    {label: "BBL", value: "BBL"},
    {label: "KTB", value: "KTB"},
  ];
  paymentGatewayChoices = [
    {label: "Omise/Opn", value: "Omise/Opn"},
    {label: "Reddot", value: "Reddot"},
    {label: "Stribe", value: "Stribe"},
    {label: "iOL Pay", value: "iOL Pay"},
  ];
  onlineShopChoices = [
    {label: "Megatix", value: "Megatix"},
    {label: "E-Voucher", value: "E-Voucher"},
    {label: "Other", value: "Other"},
  ];

  totalItems: number;
  product_id: number = 0;
  products: Product[];
  calendarLocale = CALENDAR_LOCALE.EN;

  constructor(
    private formBuilder: FormBuilder,
    private template: TemplateService,
    private number2text_th: Number2textThService,
    private apiService: ApiService,
    private translateService: TranslateService,
  ) { }

  ngOnInit() {
    this.translateService.onLangChange.subscribe(
      (event) => {
        this.setDocumentTypes();
        this.loadPaymentChoices();
        this.calendarLocale = event.lang === 'en'
          ? CALENDAR_LOCALE.EN
          : CALENDAR_LOCALE.TH;
      }
    );
    this.setDocumentTypes();
    this.loadPaymentChoices();
    this.createLineItemForm();
    this.lineItemForm.get('items').valueChanges.subscribe(() => { this.calVAT(); });
    this.lineItemForm.get('endBillDiscountList').valueChanges.subscribe(() => { this.calVAT(); } );
    this.lineItemForm.get('paymentMethods').valueChanges.subscribe(() => { this.calVAT(); } );

    // any time the inner form changes update the parent of any change
    this.createSubscriptions();
    this.getProduct()
  }

  loadPaymentChoices() {
    this.paymentChoices = [
      {
        label: this.translateService.instant("ISSUE-TEXT.PAYMENT.CASH"),
        value: 'cash',
      },
      {
        label: this.translateService.instant("ISSUE-TEXT.PAYMENT.CHEQUE"),
        value: 'cheque',
      },
      {
        label: this.translateService.instant("ISSUE-TEXT.PAYMENT.CREDIT-CARD"),
        value: 'credit_card',
      },
      {
        label: this.translateService.instant("ISSUE-TEXT.PAYMENT.BANK-TRANSFER"),
        value: 'bank_transfer',
      },
      {
        label: this.translateService.instant("ISSUE-TEXT.PAYMENT.PAYMENT-GATEWAY"),
        value: 'payment_gateway',
      },
      {
        label: this.translateService.instant("ISSUE-TEXT.PAYMENT.ONLINE-SHOP"),
        value: 'online_shop',
      },
      {
        label: this.translateService.instant("ISSUE-TEXT.PAYMENT.OTHER"),
        value: 'other',
      },
    ];
  }

  calVAT() {
    let vat = new Decimal(0);
    const vat7Amount = this.vatAmount.minus(this.vat0Amount)
    vat = vat.add(vat7Amount.mul(this.vatValue['7']));
    this.vat.setValue(vat.toDecimalPlaces(3).toDecimalPlaces(2).toString())
  }

  writeValue(value: any) {
    if (value) {
      this.lineItemForm.setValue(value);
      this.onChange(value);
      this.onTouched();
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  // communicate the inner form validation to the parent form
  validate(_control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    return new Promise(resolve =>
      this.lineItemForm.valid ? resolve(null) : resolve({ lineItemForm: { valid: false } })
    );
  }

  createSubscriptions() {
    // Subscribe to valueChanges and statusChanges
    this.subscription = combineLatest([this.lineItemForm.valueChanges, this.lineItemForm.statusChanges]).subscribe(
      () => {
        const calculateValue = {
          amountText: this.amountText,
          matchedABB: this.matchedAbbValue,
          subTotal: this.subTotal.toFixed(2),
          discountTotal: this.discountTotal.toFixed(2),
          balance: this.balance.toFixed(2),
          vat: this.vat.value ? this.vat.value : '',
          nonVATAmount: this.nonVATAmount ? this.nonVATAmount.toFixed(2) : '',
          vat0Amount: this.vat0Amount ? this.vat0Amount.toFixed(2) : '',
          vatAmount: this.vatAmount ? this.vatAmount.toFixed(2) : '',
          netTotal: this.netTotal.toFixed(2),
          correctAmount: this.correctAmount.toFixed(2),
          endOfBillDiscount: this.endOfBillDiscount.toFixed(2),
          items: (this.items.value as LineItem[]).map((item, index) => ({
            productID: item.productID,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            discount: item.discount,
            total: this.getTotal(index).toFixed(2),
            lineVAT: item.lineVAT,
            note: item.note,
          })),
          paymentMethods: (this.paymentMethods.value as paymentMethod[]).map(
            (payment) => ({
              payment_type: payment.paymentType,
              note: payment.note,
              cheque_no: payment.chequeNo,
              cheque_bank: payment.chequeBank,
              cheque_date: payment.chequeDate ? moment(payment.chequeDate).format('DD/MM/YYYY') : '',
              option_type: payment.optionType,
              transfer_date: payment.transferDate ? moment(payment.transferDate).format('DD/MM/YYYY') : '',
            })
          ),
          paymentOption: this.abbData ? this.abbData.payment_type : '',
        };

        this.onChange(Object.assign({}, this.lineItemForm.getRawValue(), calculateValue));
        this.onTouched();
      });
  }

  setDocumentTypes() {
    this.documentTypes = this.template.getDropdown();

    if (this.isCreditNote || this.isDebitNote) {
      this.documentTypes.shift();
    } else {
      this.documentTypes = this.documentTypes.filter(element => element.value === this.documentType);
    }
  }

  get isCreditNote() {
    return this.documentType === 'credit_note';
  }

  get isDebitNote() {
    return this.documentType === 'debit_note';
  }

  get isReceipt() {
    return this.documentType === 'receipt';
  }

  get isReceiptTaxInvoice() {
    return this.documentType === 'receipt_tax_invoice';
  }

  createLineItemForm() {
    this.lineItemForm = this.formBuilder.group({
      items: this.formBuilder.array([
        this.formBuilder.group({
          productID: [null],
          description: [null, Validators.required],
          quantity: ['1', Validators.required],
          unit: [null],
          unitPrice: ['0.00', Validators.required],
          discount: [null],
          lineVAT: ['7'],
          note: [null],
        })
      ]),
      endBillDiscountList: this.formBuilder.array([
        this.formBuilder.group({
          endBillDiscount: [null],
          typeDiscount: ['7']
        })
      ]),
      discountReason: [null],
      originalAmount: (this.isCreditNote || this.isDebitNote) ? [null, Validators.required] : [null],
      originalVAT: (this.isCreditNote || this.isDebitNote) ? [null, Validators.required] : [null],
      nonVATAmount: [null],
      vat0Amount: [null],
      vatAmount: [null],
      vat: [null],
      endOfBillDiscount: [null],
      percentVAT: ['7'],
      paymentOption: [null],
      paymentMethods: this.formBuilder.array([
        this.formBuilder.group({
          paymentType: [null],
          note: [null],
          chequeNo: [null],
          chequeDate: [null],
          chequeBank: [null],
          optionType: [null],
          transferDate: [null],
        }, { validator: this.validatePaymentMethods })
      ])
    });
    if (this.documentInformation && this.documentInformation.line_items) {
      this.updateLineItemsForm(this.documentInformation);
    }
  }

  validatePaymentMethods(group: FormGroup): { [key: string]: any } | null {
  const paymentType = group.get('paymentType');
  const chequeNo = group.get('chequeNo');
  const chequeDate = group.get('chequeDate');
  const chequeBank = group.get('chequeBank');
  const optionType = group.get('optionType');
  const transferDate = group.get('transferDate');

  const setRequiredError = (control: AbstractControl, required: boolean) => {
    if (required) {
      control.setErrors({ 'required': true });
    } else {
      control.setErrors(null);
    }
  };

  if (!paymentType.value) {
    setRequiredError(paymentType, true);
  } else {
    if (paymentType.value === 'cheque') {
      setRequiredError(chequeNo, !chequeNo.value);
      setRequiredError(chequeDate, !chequeDate.value);
      setRequiredError(chequeBank, !chequeBank.value);
    } else if (['credit_card', 'payment_gateway', 'online_shop'].includes(paymentType.value)) {
      setRequiredError(optionType, !optionType.value);
    } else if (paymentType.value === 'bank_transfer') {
      setRequiredError(optionType, !optionType.value);
      setRequiredError(transferDate, !transferDate.value);
    }
  }
  return null;
}

  updateLineItemsForm(documentInformation): void {
    const lineItemArray = this.lineItemForm.get('items') as FormArray;
    documentInformation.line_items.forEach((item, index) => {
      const itemValues = {
        productID: item.item_number,
        description: item.product_name,
        quantity: item.quantity,
        unit: item.unit,
        unitPrice: item.unit_price,
        discount: item.net_discount_or_charge,
        lineVAT: item.percent_vat.match(/^\d+/)[0],
        note: item.note,
      };
      try {
        lineItemArray.at(index).patchValue(itemValues);
      } catch (e) {
        lineItemArray.push(this.formBuilder.group({
          productID: [null],
          description: [null, Validators.required],
          quantity: ['1', Validators.required],
          unit: [null],
          unitPrice: ['0.00', Validators.required],
          discount: [null],
          lineVAT: ['7'],
          note: [null],
        }));
        lineItemArray.at(index).patchValue(itemValues);
      }
    });

    lineItemArray.push(this.formBuilder.group({
      productID: [null],
      description: [null, Validators.required],
      quantity: ['1', Validators.required],
      unit: [null],
      unitPrice: ['0.00', Validators.required],
      discount: [null],
      lineVAT: ['7'],
      note: [null],
    }));

    this.lineItemForm.get('vat').patchValue(documentInformation.total_items.vat);
    this.lineItemForm.get('discountReason').patchValue(documentInformation.total_items.discount_or_charge_reason);
    this.lineItemForm.get('originalAmount').patchValue(
      documentInformation.total_items.original_amount_before_vat ? documentInformation.total_items.original_amount_before_vat : null);
    this.lineItemForm.get('originalVAT').patchValue(
      documentInformation.total_items.original_amount_vat ? documentInformation.total_items.original_amount_vat : null);

    const lineItemTotalArray = this.lineItemForm.get('endBillDiscountList') as FormArray;
    lineItemTotalArray.at(0).get('endBillDiscount').patchValue(documentInformation.total_items.discount_or_charge);

    this.documentIssueForm.get('lineItemForm').patchValue(this.lineItemForm.value);
  }

  validateNegative: AsyncValidatorFn = (control: FormGroup): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
    const isNegative = Boolean(!(this.nonVATAmount.isNegative() || this.vat0Amount.isNegative() || this.vatAmount.isNegative()));
    return timer(300).pipe(
      switchMap(() => {
        return (isNegative) ? of(null) : new Promise(resolve => resolve({ vatAmountNegative: true }));
      })
    );
  }

  get items() {
    return this.lineItemForm.get('items') as FormArray;
  }

  get paymentMethods() {
    return this.lineItemForm.get('paymentMethods') as FormArray;
  }

  get endBillDiscountList() {
    return this.lineItemForm.get('endBillDiscountList') as FormArray;
  }

  get discountReason() {
    return this.lineItemForm.get('discountReason');
  }

  get originalAmount() {
    return this.lineItemForm.get('originalAmount');
  }

  get originalVAT() {
    return this.lineItemForm.get('originalVAT');
  }

  get percentVAT() {
    return this.lineItemForm.get('percentVAT');
  }

  get vat() {
    return this.lineItemForm.get('vat');
  }

  get paymentOption() {
    return this.lineItemForm.get('paymentOption');
  }

  getPaymentMethodControl(index: number, controlName: string) {
    return this.paymentMethods.controls[index].get(controlName);
  }

  showDropdownOption(index: number) {
    const paymentType = this.getPaymentMethodControl(index, 'paymentType').value || '';
    return ['credit_card', 'bank_transfer', 'payment_gateway', 'online_shop'].includes(paymentType);
  }

  showNoteBox(index: number) {
    const paymentType = this.getPaymentMethodControl(index, 'paymentType').value || '';
    return ['cash', 'online_shop', 'other'].includes(paymentType);
  }

  addPaymentMethod() {
    this.paymentMethods.push(
      this.formBuilder.group({
        paymentType: [null],
        note: [null],
        chequeNo: [null],
        chequeDate: [null],
        chequeBank: [null],
        optionType: [null],
        transferDate: [null],
      })
    );
  }

  removePaymentMethod(index: number) {
    this.paymentMethods.removeAt(index);
  }

  resetPaymentDetail(index: number) {
    this.getPaymentMethodControl(index, 'note').setValue(null);
    this.getPaymentMethodControl(index, 'chequeNo').setValue(null);
    this.getPaymentMethodControl(index, 'chequeDate').setValue(null);
    this.getPaymentMethodControl(index, 'chequeBank').setValue(null);
    this.getPaymentMethodControl(index, 'optionType').setValue(null);
    this.getPaymentMethodControl(index, 'transferDate').setValue(null);
  }

  getPaymentType(index: number) {
    return this.paymentMethods.controls[index].get('paymentType')
  }

  getPaymentChequeNO(index: number) {
    return this.paymentMethods.controls[index].get('chequeNo')
  }
  getPaymentBank(index: number) {
    return this.paymentMethods.controls[index].get('chequeBank')
  }

  getPaymentChequeDate(index: number) {
    return this.paymentMethods.controls[index].get('chequeDate')
  }

  getPaymentOptionType(index: number) {
    return this.paymentMethods.controls[index].get('optionType')
  }

  getPaymentTransferDate(index: number) {
    return this.paymentMethods.controls[index].get('transferDate')
  }

  getProductID(index: number) {
    return this.items.controls[index].get('productID');
  }

  getDescription(index: number) {
    return this.items.controls[index].get('description');
  }

  getQuantity(index: number) {
    return this.items.controls[index].get('quantity');
  }

  getUnit(index: number) {
    return this.items.controls[index].get('unit');
  }

  getUnitPrice(index: number) {
    return this.items.controls[index].get('unitPrice');
  }

  getDiscount(index: number) {
    return this.items.controls[index].get('discount');
  }

  getLineVAT(index: number) {
    return this.items.controls[index].get('lineVAT');
  }

  getTypeDiscount(index: number) {
    return this.endBillDiscountList.controls[index].get('typeDiscount');
  }

  getEndBillDiscount(index: number) {
    return this.endBillDiscountList.controls[index].get('endBillDiscount');
  }

  getTotal(index: number) {
    const quantity = new Decimal(this.getQuantity(index).value || 0);
    const unitPrice = new Decimal(this.getUnitPrice(index).value || 0);
    const discount = new Decimal(this.getDiscount(index).value || 0);
    const total = quantity.mul(unitPrice).minus(discount);

    return total.toDecimalPlaces(2);
  }

  get endOfBillDiscount() {
    let endOfBillDiscount = new Decimal(0)
    for (let index = 0; index < this.endBillDiscountList.length; index++) {
      const discount = new Decimal(this.getEndBillDiscount(index).value || 0)
      endOfBillDiscount = endOfBillDiscount.add(discount);
    }
    return endOfBillDiscount.toDecimalPlaces(2);
  }

  get subTotal() {
    let subTotal = new Decimal(0);

    for (let index = 0; index < this.items.length; index++) {
      const quantity = new Decimal(this.getQuantity(index).value || 0);
      const unitPrice = new Decimal(this.getUnitPrice(index).value || 0);

      subTotal = subTotal.add(quantity.mul(unitPrice));
    }

    return subTotal.toDecimalPlaces(2);
  }

  get vatAmount(){
    let vatAmount = new Decimal(0);
    for (let index = 0; index < this.items.length; index++) {
      if (this.getLineVAT(index).value == '7'){
        vatAmount = vatAmount.add(this.getTotal(index));
      }
    }
    for (let index = 0; index < this.endBillDiscountList.length; index++) {
      if (this.getTypeDiscount(index).value == '7'){
        const discount = new Decimal(this.getEndBillDiscount(index).value || 0)
        vatAmount = vatAmount.minus(discount);
      }
    }
    return vatAmount.toDecimalPlaces(2)
  }
  get nonVATAmount() {
    let nonVATAmount = new Decimal(0);
    for (let index = 0; index < this.items.length; index++) {
      if (this.getLineVAT(index).value == 'non-vat'){
        nonVATAmount = nonVATAmount.add(this.getTotal(index));
      }
    }
    for (let index = 0; index < this.endBillDiscountList.length; index++) {
      if (this.getTypeDiscount(index).value == 'non-vat'){
        const discount = new Decimal(this.getEndBillDiscount(index).value || 0)
        nonVATAmount = nonVATAmount.minus(discount);
      }
    }
    return nonVATAmount.toDecimalPlaces(2);
  }

  get vat0Amount() {
    let vat0Amount = new Decimal(0);
    for (let index = 0; index < this.items.length; index++) {
      if (this.getLineVAT(index).value == '0') {
        vat0Amount = vat0Amount.add(this.getTotal(index));
      }
    }
    for (let index = 0; index < this.endBillDiscountList.length; index++) {
      if (this.getTypeDiscount(index).value == '0'){
        const discount = new Decimal(this.getEndBillDiscount(index).value || 0)
        vat0Amount = vat0Amount.minus(discount);
      }
    }
    return vat0Amount.toDecimalPlaces(2);
  }

  get discountTotal() {
    let discountTotal = new Decimal(0);

    for (let index = 0; index < this.items.length; index++) {
      const discount = new Decimal(this.getDiscount(index).value || 0);

      discountTotal = discountTotal.add(discount);
    }

    return discountTotal.toDecimalPlaces(2);
  }

  get balance() {
    const balance = this.subTotal.minus(this.discountTotal).minus(this.endOfBillDiscount);
    return balance.toDecimalPlaces(2);
  }

  get netTotal() {
    const vatValue = new Decimal(this.vat.value || 0)
    let netTotal = this.balance.add(vatValue)
    return netTotal.toDecimalPlaces(2);
  }

  get correctAmount() {
    let correctAmount = new Decimal(0);

    const originalAmount = new Decimal(this.originalAmount.value || 0);

    if (this.isDebitNote) {
      correctAmount = originalAmount.add(this.balance);
    } else if (this.isCreditNote) {
      correctAmount = originalAmount.minus(this.balance);
    }

    return correctAmount.toDecimalPlaces(2);
  }

  get amountText() {
    return this.number2text_th.currencyFormat_Baht(this.netTotal.toString());
  }

  addItem() {
    this.items.push(
      this.formBuilder.group({
        productID: [null],
        description: [null, Validators.required],
        quantity: ['1', Validators.required],
        unit: [null],
        unitPrice: ['0.00', Validators.required],
        discount: [null],
        lineVAT:["7"],
        note: [null],
      })
    );
  }

  removeItem(index: number) {
    this.items.removeAt(index);
  }

  addEndBillDiscount() {
    this.endBillDiscountList.push(
      this.formBuilder.group({
        endBillDiscount: [null],
        typeDiscount:['7']
      })
    );
    if (this.endBillDiscountList.length >= 3) {
      this.isDisableAddDiscount = true
    }
  }

  removeEndBillDiscount(index: number) {
    this.endBillDiscountList.removeAt(index);
    if (this.endBillDiscountList.length < 3) {
      this.isDisableAddDiscount = false
    }
  }

  get showAbbNetTotal(): boolean {
    if (!this.abbData || typeof this.abbData === 'string') {
      return false;
    }
    return true;
  }

  get matchedAbbValue(): boolean {
    if (!this.abbData || typeof this.abbData === 'string') {
      return true;
    }
    const abbTotalPayment = new Decimal(this.abbData.total_payment);
    return this.netTotal.toFixed(2) === abbTotalPayment.toFixed(2);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  getProduct() {
    this.apiService.getProductsList()
      .subscribe((data) => {
        this.products = data;
    });
  }

  setProduct(product, item) {
    if ((item.controls.productID.value !== null
        && item.controls.description.value !== null)
        && (product.product_id === undefined || product.name_en === undefined)
        ) {

      let found: any;


      if (product.product_id === undefined) {
        found = this.products.find(x => x.name_en === item.controls.description.value);
      } else if (product.name_en === undefined) {
        found = this.products.find(x => x.id === item.controls.productID.value);
      }
      if (!found) {
        this.products.push({
          id: item.controls.productID.value,
          name_en: item.controls.description.value
        } as Product);
        product.product_id = item.controls.productID.value;
        product.name_en = item.controls.description.value;
      }
    }

    item.controls.productID.setValue(product.product_id)
    item.controls.description.setValue(product.name_en)
    item.controls.lineVAT.setValue(product.vat)
  }
}



// WEBPACK FOOTER //
// /opt/app/src/app/features/document/document-issue/components/line-item-form/line-item-form.component.ts